import MainLayout from '@components/MainLayout/MainLayout'
import LocationIcon from '@components/Location/reusable/LocationIcon'
import { Auction, Location } from '@components/Location/location.types'
import Title from '@components/Location/reusable/Title'
import BidAuctionCarousel from '@components/BidAuctionCarousel/BidAuctionCarousel'
import useMap from '@components/Location/reusable/Map/useMap'
import Map from '@components/Location/reusable/Map'
import Image from 'next/image'
import Link from 'next/link'

type Props = {
  location: Location
  auctions: Auction[]
}

const LocationSlug = (props: Props) => {
  const { auctions, location } = props

  const {
    nickName,
    address,
    city,
    state,
    zip,
    hours,
    lat,
    lng,
    contactPhone,
    contactEmail,
    taxRate,
    extendedHours,
    isFranchise,
  } = location

  const map = useMap(
    {
      allLocations: [location],
      locationsInArea: [location],
      zipCode: zip,
      zipCodePosition: { lat, lng },
    },
    { filterLocations: false }
  )

  return (
    <MainLayout
      title="Auction Location"
      previewDescription={`Dive into exciting auctions at our '${nickName}' location in ${city}, ${state}. Conveniently located at ${address}, ${zip}, we're eager to bring you exclusive deals and a vibrant bidding community. Have questions? Reach out at ${contactEmail} or call us at ${contactPhone}. Experience the thrill of bidding in ${state} with a competitive tax rate of just ${taxRate}%. Join us and explore the treasures of '${nickName}' today!`}
    >
      <header className="flex flex-row items-center gap-2 mx-auto py-6">
        <LocationIcon /> <Title>{nickName}</Title>
      </header>
      <section className="grid grid-cols-1 md:grid-cols-2 mb-8 gap-4">
        <div className="border p-4 rounded-md w-full bg-white">
          <div className="mb-4">
            <h2 className="text-md font-bold mb-1">ADDRESS</h2>
            <p className="text-gray-700">{address}</p>
            <p className="text-gray-700">
              {city}, {state} {zip}
            </p>
          </div>
          {isFranchise ? (
            <div className="mb-4">
              <h2 className="text-md font-bold mb-1">Affiliate</h2>
              <p className="text-gray-700">Independently owned and operated</p>
            </div>
          ) : (
            <></>
          )}
          <div className="mb-4">
            <h2 className="text-md font-bold mb-1">PICK UP HOURS</h2>
            <p className="text-gray-700">{hours}</p>
          </div>
          {extendedHours ? (
            <div className="mb-4">
              <h2 className="text-md font-bold mb-1"> BIDFTA+ EARLY PICK UP</h2>
              <p className="text-gray-700 inline-flex">
                {extendedHours}
                <Image
                  src="/bidfta_plus_logo.svg"
                  alt="BIDFTA+ Logo"
                  width={30}
                  height={30}
                  className="ml-1"
                />
              </p>
            </div>
          ) : (
            <></>
          )}
          <div className="mb-3">
            <h3 className="text-md font-bold mb-1">Nickname:</h3>
            <p className="text-gray-700">{nickName}</p>
          </div>
          <div className="mb-3">
            <h3 className="text-md font-bold mb-1">Contact:</h3>
            <p className="text-gray-700">Email: {contactEmail}</p>
            <p className="text-gray-700">Phone: {contactPhone}</p>
          </div>
        </div>

        <Map
          {...map}
          allLocations={[location]}
          mapContainerStyle={{ height: '50vh' }}
        />
      </section>
      <section className="">
        <h2 className="text-xl font-bold font-raleway">
          Auction ending soon at {nickName}
        </h2>
        <Link
          className={'text-bidfta-blue-light font-bold hover:underline text-16'}
          href={`/category/all/1?location=${location?.id}`}
        >
          SEE ALL AUCTIONS AT {nickName.toUpperCase()}
        </Link>
        <div className="mobile-padding mb-8 md:mb-11 mt-2">
          <BidAuctionCarousel auctions={auctions} />
        </div>
      </section>
    </MainLayout>
  )
}

export default LocationSlug
